import React from 'react';
import { GroupedTimeEntry, EntryDetail, getStatusClass } from '../../config';

interface GroupedEntryCardProps {
  groupedEntry: GroupedTimeEntry;
  isExpanded: boolean;
  entryDetails: EntryDetail[];
  projects: Array<{
    id: number;
    name: string;
    job_number: string;
  }>;
  onToggleExpanded: () => void;
  onGroupAllocate: () => void;
  onEntryHide: (entryId: number) => void;
  onEntryUpdate: (entryId: number, projectId: number | null, hours: number, isLeaveEntry?: boolean) => void;
  onEntryAllocate: (entryId: number) => void;
  onEntryUndoHide: (entryId: number) => void;
  onFetchDetails: () => Promise<void>;
}

const GroupedEntryCard: React.FC<GroupedEntryCardProps> = ({
  groupedEntry,
  isExpanded,
  entryDetails,
  projects,
  onToggleExpanded,
  onGroupAllocate,
  onEntryHide,
  onEntryUpdate,
  onEntryAllocate,
  onEntryUndoHide,
  onFetchDetails
}) => {
  // Calculate adjusted hours excluding hidden entries
  const getAdjustedHours = () => {
    if (entryDetails.length > 0) {
      // When we have entry details, calculate from actual non-hidden entries
      const nonHiddenHours = entryDetails
        .filter(entry => entry.status !== 'Hidden')
        .reduce((sum, entry) => sum + entry.hours, 0);
      return nonHiddenHours;
    } else {
      // When we don't have details, use original total
      return groupedEntry.total_hours;
    }
  };

  // Check if hours have been adjusted due to hidden entries
  const hasHiddenEntries = () => {
    return entryDetails.length > 0 && entryDetails.some(entry => entry.status === 'Hidden');
  };

  // Get individual status pills
  const getStatusPills = () => {
    const statusCounts = groupedEntry.individual_statuses.reduce((acc, status) => {
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(statusCounts)
      .filter(([_, count]) => count > 0)
      .map(([status, count]) => (
        <span 
          key={status} 
          className={`status-badge ${getStatusClass(status)}`}
          title={`${count} ${status.toLowerCase()} ${count === 1 ? 'entry' : 'entries'}`}
        >
          {count} {status}
        </span>
      ));
  };

  // Handle expansion with lazy loading
  const handleToggleExpanded = async () => {
    if (!isExpanded && entryDetails.length === 0) {
      await onFetchDetails();
    }
    onToggleExpanded();
  };

  // Only show allocate button for groups that can be allocated
  const canAllocate = groupedEntry.group_status === 'Submitted' && 
    groupedEntry.individual_statuses.some(status => status === 'Submitted');

  return (
    <div className={`grouped-entry-card ${getStatusClass(groupedEntry.group_status)}`}>
      <div className="grouped-entry-header">
        <div className="card-main-content">
          <div className="project-info">
            <div className="project-name-job">
              <h3 className="project-name">{groupedEntry.project_name}</h3>
              <span className="job-number">#{groupedEntry.job_number}</span>
            </div>
            <div className="employee-info">
              <span className="employee-name">{groupedEntry.employee_name}</span>
              <span className="week-ending">Week ending {new Date(groupedEntry.week_ending_date).toLocaleDateString()}</span>
            </div>
          </div>

          <div className="summary-metrics">
            <div className="metric">
              <span className="count">{getAdjustedHours().toFixed(2)}</span>
              <span className="label">
                hours
                {hasHiddenEntries() && (
                  <span className="hours-adjusted-indicator" title="Hours adjusted to exclude hidden entries">
                    *
                  </span>
                )}
              </span>
            </div>
            <div className="metric status-pills-container">
              {getStatusPills()}
            </div>
          </div>
        </div>

        <div className="card-actions">
          <button 
            className="toggle-button"
            onClick={handleToggleExpanded}
            aria-label={isExpanded ? 'Collapse entries' : 'Expand entries'}
          >
            <span className={`arrow ${isExpanded ? 'expanded' : ''}`}>▼</span>
            <span className="toggle-text">
              {isExpanded ? 'Hide' : 'Show'} Entries
            </span>
          </button>

          {canAllocate && (
            <button 
              className="allocate-group-button"
              onClick={onGroupAllocate}
            >
              {groupedEntry.leave_type ? 'Approve All' : 'Allocate'}
            </button>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="entry-details-section">
          <div className="entry-details-header">
            <h4>Individual Entries ({entryDetails.length})</h4>
            <p className="workflow-hint">
              Edit entries or hide individual items, then use "{groupedEntry.leave_type ? 'Approve' : 'Allocate'}" to {groupedEntry.leave_type ? 'approve' : 'allocate'} remaining entries
            </p>
          </div>
          
          <div className="entry-details-list">
            {entryDetails.map((entry) => (
              <EntryDetails
                key={entry.entry_id}
                entry={entry}
                projects={projects}
                groupStatus={groupedEntry.group_status}
                onHide={onEntryHide}
                onUpdate={onEntryUpdate}
                onAllocate={onEntryAllocate}
                onUndoHide={onEntryUndoHide}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// EntryDetails component for individual entries within the group
interface EntryDetailsProps {
  entry: EntryDetail;
  projects: Array<{
    id: number;
    name: string;
    job_number: string;
  }>;
  groupStatus: string;
  onHide: (entryId: number) => void;
  onUpdate: (entryId: number, projectId: number | null, hours: number, isLeaveEntry?: boolean) => void;
  onAllocate: (entryId: number) => void;
  onUndoHide: (entryId: number) => void;
}

const EntryDetails: React.FC<EntryDetailsProps> = ({
  entry,
  projects,
  groupStatus,
  onHide,
  onUpdate,
  onAllocate,
  onUndoHide
}) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [editedProjectId, setEditedProjectId] = React.useState<number | null>(entry.project_id);
  const [editedHours, setEditedHours] = React.useState(entry.hours);

  const handleSave = () => {
    if (editedProjectId !== entry.project_id || editedHours !== entry.hours) {
      onUpdate(entry.entry_id, editedProjectId, editedHours, entry.leave_type !== null);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedProjectId(entry.project_id);
    setEditedHours(entry.hours);
    setIsEditing(false);
  };

  const currentProject = projects.find(p => p.id === entry.project_id);

  return (
    <div className={`entry-detail ${getStatusClass(entry.status)} ${entry.status === 'Hidden' ? 'hidden' : ''}`}>
      <div className="entry-basic-info">
        <div className="entry-date-desc">
          <span className="entry-date">{new Date(entry.date).toLocaleDateString()}</span>
          <span className="entry-description">{entry.description || 'No description'}</span>
        </div>
        
        <div className="entry-project-hours">
          {isEditing ? (
            <>
              <select 
                value={editedProjectId || ""} 
                onChange={(e) => setEditedProjectId(Number(e.target.value))}
                className="project-select"
              >
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    #{project.job_number} - {project.name}
                  </option>
                ))}
              </select>
              <input
                type="number"
                value={editedHours}
                onChange={(e) => setEditedHours(Number(e.target.value))}
                step="0.25"
                min="0"
                max="24"
                className="hours-input"
              />
            </>
          ) : (
            <>
              <span className="project-display">
                #{currentProject?.job_number} - {currentProject?.name}
              </span>
              <span className="hours-display">{entry.hours}h</span>
            </>
          )}
        </div>
      </div>

      <div className="entry-actions">
        <span className={`status-badge ${getStatusClass(entry.status)}`}>
          {entry.status}
        </span>
        
        {isEditing ? (
          <div className="edit-actions">
            <button onClick={handleSave} className="save-button">Save</button>
            <button onClick={handleCancel} className="cancel-button">Cancel</button>
          </div>
        ) : (
          <div className="view-actions">
            {/* Show different actions based on group status and entry status */}
            {entry.status === 'Hidden' ? (
              <>
                {groupStatus === 'Submitted' ? (
                  // If group is submitted and entry is hidden, only show undo hide
                  <button onClick={() => onUndoHide(entry.entry_id)} className="undo-hide-button">
                    Undo Hide
                  </button>
                ) : (
                  // If group is allocated and entry is hidden, show edit and allocate
                  <>
                    <button onClick={() => setIsEditing(true)} className="edit-button">Edit</button>
                    <button onClick={() => onAllocate(entry.entry_id)} className="allocate-button">
                      {entry.leave_type ? 'Approve' : 'Allocate'}
                    </button>
                  </>
                )}
              </>
            ) : (
              // For non-hidden entries, show edit and hide
              <>
                <button onClick={() => setIsEditing(true)} className="edit-button">Edit</button>
                <button onClick={() => onHide(entry.entry_id)} className="hide-button">Hide</button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupedEntryCard; 